import { useState, useEffect } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '@/stores/auth';
import { api } from '@/services/api';
import {
    User,
    Mail,
    Shield,
    Bell,
    Palette,
    Check,
    Save,
    Moon,
    Sun,
    X,
    Loader2,
    Eye,
    EyeOff,
    Key,
    HelpCircle,
    AlertTriangle,
    Trash2,
    Users,
    Calendar,
    RefreshCw,
    Link2,
    Unlink,
} from 'lucide-react';
import { restartOnboardingTour } from '@/components/OnboardingTour';
import clsx from 'clsx';
import { usePageTitle } from '@/hooks/usePageTitle';

export default function SettingsPage() {
    const { t } = useTranslation();
    usePageTitle('nav.settings');
    const { user } = useAuthStore();
    const [activeTab, setActiveTab] = useState<'profile' | 'security' | 'notifications' | 'appearance' | 'integrations'>('profile');

    const tabs = [
        { id: 'profile' as const, label: t('settings.profile'), icon: User },
        { id: 'team' as const, label: t('settings.team', 'Team'), icon: Users, href: '/settings/team' },
        { id: 'security' as const, label: t('settings.security'), icon: Shield },
        { id: 'notifications' as const, label: t('settings.notifications'), icon: Bell },
        { id: 'integrations' as const, label: t('settings.integrations', 'Integrations'), icon: Calendar },
        { id: 'appearance' as const, label: t('settings.appearance'), icon: Palette },
    ];

    return (
        <div className="max-w-4xl mx-auto space-y-6">
            {/* Header */}
            <div>
                <h1 className="text-2xl font-bold">{t('settings.title')}</h1>
                <p className="text-neutral-600 dark:text-neutral-400">
                    {t('settings.subtitle')}
                </p>
            </div>

            <div className="flex flex-col md:flex-row gap-6">
                {/* Sidebar */}
                <div className="w-full md:w-48 flex-shrink-0">
                    <nav className="flex md:flex-col gap-1">
                        {tabs.map((tab) => (
                            tab.href ? (
                                <a
                                    key={tab.id}
                                    href={tab.href}
                                    className={clsx(
                                        'flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                                        'text-neutral-600 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-700'
                                    )}
                                >
                                    <tab.icon className="w-4 h-4" />
                                    {tab.label}
                                </a>
                            ) : (
                                <button
                                    key={tab.id}
                                    onClick={() => setActiveTab(tab.id as typeof activeTab)}
                                    className={clsx(
                                        'flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                                        activeTab === tab.id
                                            ? 'bg-primary-50 dark:bg-primary-600/20 text-primary-600'
                                            : 'text-neutral-600 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-700'
                                    )}
                                >
                                    <tab.icon className="w-4 h-4" />
                                    {tab.label}
                                </button>
                            )
                        ))}
                    </nav>
                </div>

                {/* Content */}
                <div className="flex-1">
                    {activeTab === 'profile' && <ProfileSettings user={user} />}
                    {activeTab === 'security' && <SecuritySettings />}
                    {activeTab === 'notifications' && <NotificationSettings />}
                    {activeTab === 'integrations' && <IntegrationsSettings />}
                    {activeTab === 'appearance' && <AppearanceSettings />}
                </div>
            </div>
        </div>
    );
}

function ProfileSettings({ user }: { user: any }) {
    const { t } = useTranslation();
    const [showPasswordModal, setShowPasswordModal] = useState(false);
    const [formData, setFormData] = useState({
        full_name: user?.full_name || '',
        email: user?.email || '',
    });
    const [saved, setSaved] = useState(false);

    const handleSave = () => {
        setSaved(true);
        setTimeout(() => setSaved(false), 2000);
    };

    return (
        <>
            <div className="card p-6 space-y-6">
                <div>
                    <h2 className="text-lg font-semibold mb-4">{t('settings.profileInfo')}</h2>

                    {/* Avatar */}
                    <div className="flex items-center gap-4 mb-6">
                        <div className="w-16 h-16 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-full flex items-center justify-center">
                            <span className="text-white text-2xl font-bold">
                                {user?.full_name?.charAt(0) || 'U'}
                            </span>
                        </div>
                        <div>
                            <p className="font-medium">{user?.full_name}</p>
                            <p className="text-sm text-neutral-500">{user?.email}</p>
                        </div>
                    </div>

                    <div className="space-y-4">
                        <div>
                            <label className="label flex items-center gap-1">
                                <User className="w-3.5 h-3.5" /> {t('auth.fullName')}
                            </label>
                            <input
                                type="text"
                                value={formData.full_name}
                                onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                                className="input"
                            />
                        </div>

                        <div>
                            <label className="label flex items-center gap-1">
                                <Mail className="w-3.5 h-3.5" /> Email
                            </label>
                            <input
                                type="email"
                                value={formData.email}
                                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                className="input"
                                disabled
                            />
                            <p className="text-xs text-neutral-500 mt-1">{t('settings.emailCannotChange')}</p>
                        </div>
                    </div>
                </div>

                <div className="pt-4 border-t border-neutral-200 dark:border-neutral-700">
                    <button onClick={handleSave} className="btn-primary">
                        {saved ? (
                            <>
                                <Check className="w-4 h-4" />
                                {t('settings.saved')}
                            </>
                        ) : (
                            <>
                                <Save className="w-4 h-4" />
                                {t('settings.saveChanges')}
                            </>
                        )}
                    </button>
                </div>


                {/* Help Section */}
                <div className="pt-6 border-t border-neutral-200 dark:border-neutral-700">
                    <h3 className="text-md font-semibold mb-4 flex items-center gap-2">
                        <HelpCircle className="w-4 h-4" /> Trợ giúp
                    </h3>
                    <div className="space-y-3">
                        <button
                            onClick={restartOnboardingTour}
                            className="btn-secondary w-full justify-start"
                        >
                            <HelpCircle className="w-4 h-4" />
                            Xem lại Hướng dẫn sử dụng
                        </button>
                    </div>
                </div>
            </div>

            {/* Change Password Modal */}
            {showPasswordModal && (
                <ChangePasswordModal onClose={() => setShowPasswordModal(false)} />
            )}
        </>
    );
}

function ChangePasswordModal({ onClose }: { onClose: () => void }) {
    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [formData, setFormData] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
    });
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);

    const mutation = useMutation({
        mutationFn: () => api.changePassword(formData.currentPassword, formData.newPassword),
        onSuccess: () => {
            setSuccess(true);
            setTimeout(() => {
                onClose();
            }, 1500);
        },
        onError: (err: any) => {
            setError(err.message || 'Failed to change password');
        },
    });

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        if (formData.newPassword !== formData.confirmPassword) {
            setError('New passwords do not match');
            return;
        }

        if (formData.newPassword.length < 8) {
            setError('New password must be at least 8 characters');
            return;
        }

        mutation.mutate();
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
            <div className="bg-white dark:bg-neutral-800 rounded-xl shadow-xl w-full max-w-md animate-scale-in">
                <div className="flex items-center justify-between p-4 border-b border-neutral-200 dark:border-neutral-700">
                    <h2 className="text-lg font-semibold flex items-center gap-2">
                        <Key className="w-5 h-5" />
                        Change Password
                    </h2>
                    <button onClick={onClose} className="p-1 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="p-4 space-y-4">
                    {success ? (
                        <div className="py-8 text-center">
                            <div className="w-16 h-16 bg-success-100 dark:bg-success-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
                                <Check className="w-8 h-8 text-success-600" />
                            </div>
                            <p className="text-lg font-medium">Password Changed!</p>
                            <p className="text-sm text-neutral-500">Your password has been updated successfully.</p>
                        </div>
                    ) : (
                        <>
                            <div>
                                <label className="label">Current Password</label>
                                <div className="relative">
                                    <input
                                        type={showCurrentPassword ? 'text' : 'password'}
                                        value={formData.currentPassword}
                                        onChange={(e) => setFormData({ ...formData, currentPassword: e.target.value })}
                                        className="input pr-10"
                                        required
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400"
                                    >
                                        {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                    </button>
                                </div>
                            </div>

                            <div>
                                <label className="label">New Password</label>
                                <div className="relative">
                                    <input
                                        type={showNewPassword ? 'text' : 'password'}
                                        value={formData.newPassword}
                                        onChange={(e) => setFormData({ ...formData, newPassword: e.target.value })}
                                        className="input pr-10"
                                        minLength={8}
                                        required
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowNewPassword(!showNewPassword)}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400"
                                    >
                                        {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                    </button>
                                </div>
                                <p className="text-xs text-neutral-500 mt-1">At least 8 characters</p>
                            </div>

                            <div>
                                <label className="label">Confirm New Password</label>
                                <input
                                    type="password"
                                    value={formData.confirmPassword}
                                    onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                                    className="input"
                                    required
                                />
                            </div>

                            {error && (
                                <div className="p-3 bg-danger-50 dark:bg-danger-600/20 text-danger-600 text-sm rounded-lg">
                                    {error}
                                </div>
                            )}

                            <div className="flex justify-end gap-2 pt-2">
                                <button type="button" onClick={onClose} className="btn-secondary">
                                    Cancel
                                </button>
                                <button type="submit" disabled={mutation.isPending} className="btn-primary">
                                    {mutation.isPending ? (
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : (
                                        <>
                                            <Key className="w-4 h-4" />
                                            Change Password
                                        </>
                                    )}
                                </button>
                            </div>
                        </>
                    )}
                </form>
            </div>
        </div>
    );
}

function SecuritySettings() {
    const { t } = useTranslation();
    const [twoFAStatus, setTwoFAStatus] = useState<{
        enabled: boolean;
        enabled_at: string | null;
        recovery_codes_remaining: number;
    } | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [setupStep, setSetupStep] = useState<'idle' | 'setup' | 'verify' | 'recovery'>('idle');
    const [setupData, setSetupData] = useState<{ secret: string; qr_code_url: string } | null>(null);
    const [verifyCode, setVerifyCode] = useState('');
    const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
    const [error, setError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showDisableModal, setShowDisableModal] = useState(false);
    const [showPasswordModal, setShowPasswordModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);

    // Fetch 2FA status
    useEffect(() => {
        const fetch2FAStatus = async () => {
            try {
                const status = await api.get2FAStatus();
                setTwoFAStatus(status);
            } catch (err) {
                console.error('Failed to get 2FA status:', err);
            } finally {
                setIsLoading(false);
            }
        };
        fetch2FAStatus();
    }, []);

    const handleSetup2FA = async () => {
        setError('');
        setIsSubmitting(true);
        try {
            const data = await api.setup2FA();
            setSetupData(data);
            setSetupStep('setup');
        } catch (err: any) {
            setError(err.message || 'Failed to setup 2FA');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleVerifySetup = async () => {
        if (verifyCode.length !== 6) {
            setError('Please enter a 6-digit code');
            return;
        }
        setError('');
        setIsSubmitting(true);
        try {
            const result = await api.verifySetup2FA(verifyCode);
            setRecoveryCodes(result.recovery_codes);
            setSetupStep('recovery');
        } catch (err: any) {
            setError(err.message || 'Invalid code');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleFinishSetup = () => {
        setSetupStep('idle');
        setTwoFAStatus({
            enabled: true,
            enabled_at: new Date().toISOString(),
            recovery_codes_remaining: 10,
        });
        setVerifyCode('');
        setSetupData(null);
        setRecoveryCodes([]);
    };

    const downloadRecoveryCodes = () => {
        const content = `VisionBoard Recovery Codes\n${'='.repeat(40)}\n\nKeep these codes safe. Each can only be used once.\n\n${recoveryCodes.map((code, i) => `${i + 1}. ${code}`).join('\n')}\n\nGenerated: ${new Date().toLocaleString()}`;
        const blob = new Blob([content], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'visionboard-recovery-codes.txt';
        a.click();
        URL.revokeObjectURL(url);
    };

    if (isLoading) {
        return (
            <div className="card p-6 flex items-center justify-center">
                <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
            </div>
        );
    }

    return (
        <>
            <div className="card p-6 space-y-6">
                <div>
                    <h2 className="text-lg font-semibold mb-2 flex items-center gap-2">
                        <Shield className="w-5 h-5" />
                        {t('twoFactor.title', 'Two-Factor Authentication')}
                    </h2>
                    <p className="text-sm text-neutral-600 dark:text-neutral-400">
                        {t('twoFactor.setup.description', 'Add an extra layer of security to your account')}
                    </p>
                </div>

                {error && (
                    <div className="p-3 bg-danger-50 dark:bg-danger-600/20 text-danger-600 text-sm rounded-lg">
                        {error}
                    </div>
                )}

                {setupStep === 'idle' && (
                    <>
                        {twoFAStatus?.enabled ? (
                            <div className="space-y-4">
                                <div className="flex items-center gap-3 p-4 bg-success-50 dark:bg-success-600/20 rounded-lg">
                                    <div className="w-10 h-10 bg-success-100 dark:bg-success-600/30 rounded-full flex items-center justify-center">
                                        <Check className="w-5 h-5 text-success-600" />
                                    </div>
                                    <div>
                                        <p className="font-medium text-success-700 dark:text-success-400">
                                            {t('twoFactor.status.enabled', '2FA is enabled')}
                                        </p>
                                        <p className="text-sm text-success-600 dark:text-success-500">
                                            {t('twoFactor.recoveryCodes.remaining', 'Recovery codes remaining')}: {twoFAStatus.recovery_codes_remaining}
                                        </p>
                                    </div>
                                </div>
                                <button
                                    onClick={() => setShowDisableModal(true)}
                                    className="btn-ghost text-danger-600 hover:bg-danger-50"
                                >
                                    {t('twoFactor.disable.button', 'Disable 2FA')}
                                </button>
                            </div>
                        ) : (
                            <button
                                onClick={handleSetup2FA}
                                disabled={isSubmitting}
                                className="btn-primary"
                            >
                                {isSubmitting ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                    <>
                                        <Shield className="w-4 h-4" />
                                        {t('twoFactor.setup.enable', 'Enable 2FA')}
                                    </>
                                )}
                            </button>
                        )}
                    </>
                )}

                {setupStep === 'setup' && setupData && (
                    <div className="space-y-6">
                        <div className="text-center">
                            <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-4">
                                {t('twoFactor.setup.scanQR', 'Scan this QR code with your authenticator app')}
                            </p>
                            <div className="bg-white p-4 rounded-lg inline-block mb-4">
                                <img
                                    src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(setupData.qr_code_url)}`}
                                    alt="2FA QR Code"
                                    className="w-48 h-48"
                                />
                            </div>
                            <p className="text-sm text-neutral-500">
                                {t('twoFactor.setup.manualEntry', "Or enter this key manually:")}
                            </p>
                            <code className="block mt-2 px-4 py-2 bg-neutral-100 dark:bg-neutral-700 rounded text-sm font-mono break-all">
                                {setupData.secret}
                            </code>
                        </div>

                        <div>
                            <label className="label">
                                {t('twoFactor.setup.enterCode', 'Enter the 6-digit code shown in your app')}
                            </label>
                            <input
                                type="text"
                                value={verifyCode}
                                onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                className="input text-center text-2xl tracking-widest"
                                placeholder="000000"
                                maxLength={6}
                            />
                        </div>

                        <div className="flex gap-2">
                            <button
                                onClick={() => {
                                    setSetupStep('idle');
                                    setSetupData(null);
                                    setVerifyCode('');
                                    setError('');
                                }}
                                className="btn-secondary flex-1"
                            >
                                {t('common.cancel', 'Cancel')}
                            </button>
                            <button
                                onClick={handleVerifySetup}
                                disabled={verifyCode.length !== 6 || isSubmitting}
                                className="btn-primary flex-1"
                            >
                                {isSubmitting ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                    t('twoFactor.verifyButton', 'Verify')
                                )}
                            </button>
                        </div>
                    </div>
                )}

                {setupStep === 'recovery' && (
                    <div className="space-y-6">
                        <div className="text-center">
                            <div className="w-16 h-16 bg-success-100 dark:bg-success-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
                                <Check className="w-8 h-8 text-success-600" />
                            </div>
                            <h3 className="text-lg font-semibold mb-2">
                                {t('twoFactor.recoveryCodes.title', 'Save Your Recovery Codes')}
                            </h3>
                            <p className="text-sm text-neutral-600 dark:text-neutral-400">
                                {t('twoFactor.recoveryCodes.warning', "Store these codes safely. Each can only be used once. You won't be able to see them again.")}
                            </p>
                        </div>

                        <div className="grid grid-cols-2 gap-2 p-4 bg-neutral-100 dark:bg-neutral-700 rounded-lg">
                            {recoveryCodes.map((code, i) => (
                                <code key={i} className="text-sm font-mono text-center py-1">
                                    {code}
                                </code>
                            ))}
                        </div>

                        <div className="flex gap-2">
                            <button
                                onClick={downloadRecoveryCodes}
                                className="btn-secondary flex-1"
                            >
                                {t('twoFactor.recoveryCodes.downloadButton', 'Download Codes')}
                            </button>
                            <button
                                onClick={handleFinishSetup}
                                className="btn-primary flex-1"
                            >
                                {t('common.done', 'Done')}
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Password Section */}
            <div className="card p-6">
                <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <Key className="w-5 h-5" />
                    {t('settings.changePassword', 'Change Password')}
                </h2>
                <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-4">
                    {t('settings.passwordDescription', 'Update your password to keep your account secure')}
                </p>
                <button
                    onClick={() => setShowPasswordModal(true)}
                    className="btn-secondary"
                >
                    <Key className="w-4 h-4" />
                    {t('settings.changePassword', 'Change Password')}
                </button>
            </div>

            {/* Danger Zone */}
            <div className="card p-6 border-danger-200 dark:border-danger-800">
                <h2 className="text-lg font-semibold mb-4 flex items-center gap-2 text-danger-600">
                    <AlertTriangle className="w-5 h-5" />
                    {t('settings.dangerZone', 'Danger Zone')}
                </h2>
                <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-4">
                    {t('settings.deleteAccountWarning', 'Once you delete your account, there is no going back. Please be certain.')}
                </p>
                <button
                    onClick={() => setShowDeleteModal(true)}
                    className="btn-ghost text-danger-600 hover:bg-danger-50 dark:hover:bg-danger-600/20"
                >
                    {t('settings.deleteAccount', 'Delete Account')}
                </button>
            </div>

            {showDisableModal && (
                <Disable2FAModal
                    onClose={() => setShowDisableModal(false)}
                    onDisabled={() => {
                        setShowDisableModal(false);
                        setTwoFAStatus({
                            enabled: false,
                            enabled_at: null,
                            recovery_codes_remaining: 0,
                        });
                    }}
                    isOAuthUser={useAuthStore.getState().user?.auth_provider !== 'local'}
                />
            )}

            {showPasswordModal && (
                <ChangePasswordModal onClose={() => setShowPasswordModal(false)} />
            )}

            {showDeleteModal && (
                <DeleteAccountModal onClose={() => setShowDeleteModal(false)} />
            )}
        </>
    );
}

function Disable2FAModal({ onClose, onDisabled, isOAuthUser = false }: { onClose: () => void; onDisabled: () => void; isOAuthUser?: boolean }) {
    const { t } = useTranslation();
    const [code, setCode] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleDisable = async () => {
        if (code.length !== 6) {
            setError('Please enter a 6-digit code');
            return;
        }
        setError('');
        setIsSubmitting(true);
        try {
            await api.disable2FA(code, password || undefined);
            onDisabled();
        } catch (err: any) {
            setError(err.message || 'Failed to disable 2FA');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
            <div className="bg-white dark:bg-neutral-800 rounded-xl shadow-xl w-full max-w-md animate-scale-in">
                <div className="flex items-center justify-between p-4 border-b border-neutral-200 dark:border-neutral-700">
                    <h2 className="text-lg font-semibold flex items-center gap-2 text-danger-600">
                        <Shield className="w-5 h-5" />
                        {t('twoFactor.disable.title', 'Disable 2FA')}
                    </h2>
                    <button onClick={onClose} className="p-1 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="p-4 space-y-4">
                    <p className="text-sm text-neutral-600 dark:text-neutral-400">
                        {t('twoFactor.disable.warning', 'This will remove 2FA protection from your account')}
                    </p>

                    <div>
                        <label className="label">Current 2FA Code</label>
                        <input
                            type="text"
                            value={code}
                            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                            className="input text-center text-lg tracking-widest"
                            placeholder="000000"
                            maxLength={6}
                        />
                    </div>

                    {!isOAuthUser && (
                        <div>
                            <label className="label">Password (optional for extra security)</label>
                            <input
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="input"
                                placeholder="Your password"
                            />
                        </div>
                    )}

                    {error && (
                        <div className="p-3 bg-danger-50 dark:bg-danger-600/20 text-danger-600 text-sm rounded-lg">
                            {error}
                        </div>
                    )}

                    <div className="flex gap-2 pt-2">
                        <button onClick={onClose} className="btn-secondary flex-1">
                            {t('common.cancel', 'Cancel')}
                        </button>
                        <button
                            onClick={handleDisable}
                            disabled={code.length !== 6 || isSubmitting}
                            className="btn-primary flex-1 bg-danger-600 hover:bg-danger-700"
                        >
                            {isSubmitting ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                                t('twoFactor.disable.button', 'Disable 2FA')
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

function DeleteAccountModal({ onClose }: { onClose: () => void }) {
    const { t } = useTranslation();
    const { logout } = useAuthStore();
    const [password, setPassword] = useState('');
    const [confirmText, setConfirmText] = useState('');
    const [error, setError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleDelete = async () => {
        if (confirmText !== 'DELETE') {
            setError('Please type DELETE to confirm');
            return;
        }
        if (!password) {
            setError('Password is required');
            return;
        }

        setError('');
        setIsSubmitting(true);
        try {
            await api.deleteAccount(password);
            logout();
            window.location.href = '/';
        } catch (err: any) {
            setError(err.message || 'Failed to delete account');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
            <div className="bg-white dark:bg-neutral-800 rounded-xl shadow-xl w-full max-w-md animate-scale-in">
                <div className="flex items-center justify-between p-4 border-b border-neutral-200 dark:border-neutral-700">
                    <h2 className="text-lg font-semibold flex items-center gap-2 text-danger-600">
                        <Trash2 className="w-5 h-5" />
                        {t('settings.deleteAccount', 'Delete Account')}
                    </h2>
                    <button onClick={onClose} className="p-1 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="p-4 space-y-4">
                    <div className="p-3 bg-danger-50 dark:bg-danger-600/20 rounded-lg">
                        <p className="text-sm text-danger-600 font-medium flex items-center gap-2">
                            <AlertTriangle className="w-4 h-4" />
                            {t('settings.deleteWarningTitle', 'This action cannot be undone')}
                        </p>
                        <p className="text-xs text-danger-500 mt-1">
                            {t('settings.deleteWarningText', 'All your data including workspaces, boards, tasks, and goals will be permanently deleted.')}
                        </p>
                    </div>

                    <div>
                        <label className="label">{t('auth.password', 'Password')}</label>
                        <input
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className="input"
                            placeholder={t('settings.enterPassword', 'Enter your password')}
                        />
                    </div>

                    <div>
                        <label className="label">
                            {t('settings.typeDelete', 'Type DELETE to confirm')}
                        </label>
                        <input
                            type="text"
                            value={confirmText}
                            onChange={(e) => setConfirmText(e.target.value.toUpperCase())}
                            className="input"
                            placeholder="DELETE"
                        />
                    </div>

                    {error && (
                        <div className="p-3 bg-danger-50 dark:bg-danger-600/20 text-danger-600 text-sm rounded-lg">
                            {error}
                        </div>
                    )}

                    <div className="flex gap-2 pt-2">
                        <button onClick={onClose} className="btn-secondary flex-1">
                            {t('common.cancel', 'Cancel')}
                        </button>
                        <button
                            onClick={handleDelete}
                            disabled={confirmText !== 'DELETE' || !password || isSubmitting}
                            className="btn-primary flex-1 bg-danger-600 hover:bg-danger-700 disabled:opacity-50"
                        >
                            {isSubmitting ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                                <>
                                    <Trash2 className="w-4 h-4" />
                                    {t('settings.deleteAccount', 'Delete Account')}
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

function NotificationSettings() {
    const { t } = useTranslation();
    const [settings, setSettings] = useState({
        email_daily_digest: true,
        email_task_due: true,
        email_kr_at_risk: true,
        push_drift_alert: true,
        push_mentions: true,
        quiet_hours: false,
    });
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);

    // Fetch preferences on mount
    useEffect(() => {
        const fetchPreferences = async () => {
            try {
                const response = await api.getNotificationPreferences();
                if (response.success && response.data) {
                    setSettings(response.data);
                }
            } catch (error) {
                console.error('Failed to fetch notification preferences:', error);
            } finally {
                setIsLoading(false);
            }
        };
        fetchPreferences();
    }, []);

    const toggleSetting = async (key: keyof typeof settings) => {
        const newValue = !settings[key];
        setSettings({ ...settings, [key]: newValue });
        setIsSaving(true);

        try {
            await api.updateNotificationPreferences({ [key]: newValue });
        } catch (error) {
            // Revert on error
            setSettings({ ...settings, [key]: !newValue });
            console.error('Failed to update notification preference:', error);
        } finally {
            setIsSaving(false);
        }
    };

    const ToggleSwitch = ({ checked, onChange, disabled }: { checked: boolean; onChange: () => void; disabled?: boolean }) => (
        <button
            onClick={onChange}
            disabled={disabled}
            className={clsx(
                'relative w-11 h-6 rounded-full transition-colors',
                checked ? 'bg-primary-600' : 'bg-neutral-300 dark:bg-neutral-600',
                disabled && 'opacity-50 cursor-not-allowed'
            )}
        >
            <span
                className={clsx(
                    'absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform',
                    checked && 'translate-x-5'
                )}
            />
        </button>
    );

    if (isLoading) {
        return (
            <div className="card p-6 flex items-center justify-center">
                <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
            </div>
        );
    }

    return (
        <div className="card p-6 space-y-6">
            <div>
                <h2 className="text-lg font-semibold mb-4">{t('settings.emailNotifications')}</h2>
                <div className="space-y-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="font-medium text-sm">{t('settings.dailyDigest')}</p>
                            <p className="text-xs text-neutral-500">{t('settings.dailyDigestDesc')}</p>
                        </div>
                        <ToggleSwitch
                            checked={settings.email_daily_digest}
                            onChange={() => toggleSetting('email_daily_digest')}
                            disabled={isSaving}
                        />
                    </div>
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="font-medium text-sm">{t('settings.taskDueReminders')}</p>
                            <p className="text-xs text-neutral-500">{t('settings.taskDueRemindersDesc')}</p>
                        </div>
                        <ToggleSwitch
                            checked={settings.email_task_due}
                            onChange={() => toggleSetting('email_task_due')}
                            disabled={isSaving}
                        />
                    </div>
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="font-medium text-sm">{t('settings.krAtRiskAlerts')}</p>
                            <p className="text-xs text-neutral-500">{t('settings.krAtRiskAlertsDesc')}</p>
                        </div>
                        <ToggleSwitch
                            checked={settings.email_kr_at_risk}
                            onChange={() => toggleSetting('email_kr_at_risk')}
                            disabled={isSaving}
                        />
                    </div>
                </div>
            </div>

            <div className="pt-6 border-t border-neutral-200 dark:border-neutral-700">
                <h2 className="text-lg font-semibold mb-4">{t('settings.inAppNotifications')}</h2>
                <div className="space-y-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="font-medium text-sm">{t('settings.driftAlerts')}</p>
                            <p className="text-xs text-neutral-500">{t('settings.driftAlertsDesc')}</p>
                        </div>
                        <ToggleSwitch
                            checked={settings.push_drift_alert}
                            onChange={() => toggleSetting('push_drift_alert')}
                            disabled={isSaving}
                        />
                    </div>
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="font-medium text-sm">{t('settings.mentions')}</p>
                            <p className="text-xs text-neutral-500">{t('settings.mentionsDesc')}</p>
                        </div>
                        <ToggleSwitch
                            checked={settings.push_mentions}
                            onChange={() => toggleSetting('push_mentions')}
                            disabled={isSaving}
                        />
                    </div>
                </div>
            </div>

            <div className="pt-6 border-t border-neutral-200 dark:border-neutral-700">
                <div className="flex items-center justify-between">
                    <div>
                        <p className="font-medium text-sm">{t('settings.quietHours')}</p>
                        <p className="text-xs text-neutral-500">{t('settings.quietHoursDesc')}</p>
                    </div>
                    <ToggleSwitch
                        checked={settings.quiet_hours}
                        onChange={() => toggleSetting('quiet_hours')}
                        disabled={isSaving}
                    />
                </div>
            </div>
        </div>
    );
}

function AppearanceSettings() {
    const { i18n } = useTranslation();
    const { t } = useTranslation();
    const [theme, setTheme] = useState<'light' | 'dark' | 'system'>(() => {
        // Load from localStorage on mount
        const saved = localStorage.getItem('theme') as 'light' | 'dark' | 'system' | null;
        return saved || 'system';
    });

    const languages = [
        { code: 'en', name: 'English', flag: '🇺🇸' },
        { code: 'vi', name: 'Tiếng Việt', flag: '🇻🇳' },
    ];

    // Apply theme changes
    useEffect(() => {
        const applyTheme = (mode: 'light' | 'dark' | 'system') => {
            const root = document.documentElement;

            if (mode === 'system') {
                const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
                root.classList.toggle('dark', prefersDark);
                localStorage.setItem('theme', 'system');
            } else {
                root.classList.toggle('dark', mode === 'dark');
                localStorage.setItem('theme', mode);
            }
        };

        applyTheme(theme);

        // Listen for system theme changes when mode is 'system'
        if (theme === 'system') {
            const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
            const handler = (e: MediaQueryListEvent) => {
                document.documentElement.classList.toggle('dark', e.matches);
            };

            mediaQuery.addEventListener('change', handler);
            return () => mediaQuery.removeEventListener('change', handler);
        }
    }, [theme]);

    return (
        <div className="card p-6 space-y-6">
            {/* Language */}
            <div>
                <h2 className="text-lg font-semibold mb-4">{t('settings.language')}</h2>
                <div className="flex gap-3">
                    {languages.map((lang) => (
                        <button
                            key={lang.code}
                            onClick={() => i18n.changeLanguage(lang.code)}
                            className={clsx(
                                'flex items-center gap-2 px-4 py-3 rounded-lg border-2 transition-all',
                                i18n.language === lang.code
                                    ? 'border-primary-500 bg-primary-50 dark:bg-primary-600/20'
                                    : 'border-neutral-200 dark:border-neutral-700 hover:border-primary-300'
                            )}
                        >
                            <span className="text-2xl">{lang.flag}</span>
                            <span className={clsx(
                                'font-medium',
                                i18n.language === lang.code ? 'text-primary-600' : 'text-neutral-600 dark:text-neutral-400'
                            )}>
                                {lang.name}
                            </span>
                        </button>
                    ))}
                </div>
            </div>

            {/* Theme */}
            <div className="pt-6 border-t border-neutral-200 dark:border-neutral-700">
                <h2 className="text-lg font-semibold mb-4">{t('settings.theme')}</h2>
                <div className="grid grid-cols-3 gap-3">
                    {[
                        { id: 'light' as const, label: t('settings.themeLight'), icon: Sun },
                        { id: 'dark' as const, label: t('settings.themeDark'), icon: Moon },
                        { id: 'system' as const, label: t('settings.themeSystem'), icon: Palette },
                    ].map((option) => (
                        <button
                            key={option.id}
                            onClick={() => setTheme(option.id)}
                            className={clsx(
                                'p-4 rounded-lg border-2 transition-all flex flex-col items-center gap-2',
                                theme === option.id
                                    ? 'border-primary-500 bg-primary-50 dark:bg-primary-600/20'
                                    : 'border-neutral-200 dark:border-neutral-700 hover:border-primary-300'
                            )}
                        >
                            <option.icon className={clsx(
                                'w-6 h-6',
                                theme === option.id ? 'text-primary-600' : 'text-neutral-500'
                            )} />
                            <span className={clsx(
                                'text-sm font-medium',
                                theme === option.id ? 'text-primary-600' : 'text-neutral-600 dark:text-neutral-400'
                            )}>
                                {option.label}
                            </span>
                        </button>
                    ))}
                </div>
            </div>

            <div className="pt-6 border-t border-neutral-200 dark:border-neutral-700">
                <h2 className="text-lg font-semibold mb-4">Accent Color</h2>
                <div className="flex gap-3">
                    {[
                        { color: 'bg-primary-500', name: 'Blue' },
                        { color: 'bg-success-500', name: 'Green' },
                        { color: 'bg-secondary-500', name: 'Purple' },
                        { color: 'bg-warning-500', name: 'Orange' },
                        { color: 'bg-danger-500', name: 'Red' },
                    ].map((c, i) => (
                        <button
                            key={i}
                            className={clsx(
                                'w-10 h-10 rounded-full',
                                c.color,
                                i === 0 && 'ring-2 ring-offset-2 ring-primary-500'
                            )}
                            title={c.name}
                        />
                    ))}
                </div>
                <p className="text-xs text-neutral-500 mt-2">Coming soon</p>
            </div>
        </div>
    );
}

// ============================================
// INTEGRATIONS SETTINGS - Google Calendar
// ============================================
function IntegrationsSettings() {
    const { t } = useTranslation();
    const [status, setStatus] = useState<{
        connected: boolean;
        syncEnabled: boolean;
        calendarId: string | null;
    } | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isConnecting, setIsConnecting] = useState(false);
    const [isSyncing, setIsSyncing] = useState(false);
    const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

    // Check for OAuth callback code in URL
    useEffect(() => {
        const checkCallback = async () => {
            const params = new URLSearchParams(window.location.search);
            const code = params.get('code');
            if (code && params.get('tab') === 'integrations') {
                setIsConnecting(true);
                try {
                    await api.handleCalendarCallback(code);
                    setMessage({ type: 'success', text: 'Google Calendar connected successfully!' });
                    // Clean URL
                    window.history.replaceState({}, '', window.location.pathname);
                    // Refresh status
                    const newStatus = await api.getCalendarStatus();
                    setStatus(newStatus);
                } catch (err: any) {
                    setMessage({ type: 'error', text: err.message || 'Failed to connect' });
                } finally {
                    setIsConnecting(false);
                }
            }
        };
        checkCallback();
    }, []);

    // Fetch status
    useEffect(() => {
        const fetchStatus = async () => {
            try {
                const data = await api.getCalendarStatus();
                setStatus(data);
            } catch (error) {
                console.error('Failed to get calendar status:', error);
            } finally {
                setIsLoading(false);
            }
        };
        fetchStatus();
    }, []);

    const handleConnect = async () => {
        setIsConnecting(true);
        try {
            const { authUrl } = await api.getCalendarConnectUrl();
            window.location.href = authUrl;
        } catch (err: any) {
            setMessage({ type: 'error', text: err.message || 'Failed to connect' });
            setIsConnecting(false);
        }
    };

    const handleDisconnect = async () => {
        if (!confirm('Are you sure you want to disconnect Google Calendar?')) return;
        try {
            await api.disconnectCalendar();
            setStatus({ connected: false, syncEnabled: false, calendarId: null });
            setMessage({ type: 'success', text: 'Google Calendar disconnected' });
        } catch (err: any) {
            setMessage({ type: 'error', text: err.message || 'Failed to disconnect' });
        }
    };

    const handleSyncAll = async () => {
        setIsSyncing(true);
        try {
            const result = await api.syncAllTasksToCalendar();
            setMessage({ type: 'success', text: `Synced ${result.synced} tasks to Google Calendar` });
        } catch (err: any) {
            setMessage({ type: 'error', text: err.message || 'Failed to sync' });
        } finally {
            setIsSyncing(false);
        }
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-primary-500" />
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div>
                <h2 className="text-lg font-semibold mb-1">{t('settings.integrations', 'Integrations')}</h2>
                <p className="text-sm text-neutral-600 dark:text-neutral-400">
                    Connect external services to sync your tasks and deadlines.
                </p>
            </div>

            {message && (
                <div className={clsx(
                    'p-3 rounded-lg text-sm',
                    message.type === 'success'
                        ? 'bg-success-50 dark:bg-success-600/20 text-success-600'
                        : 'bg-danger-50 dark:bg-danger-600/20 text-danger-600'
                )}>
                    {message.text}
                </div>
            )}

            {/* Google Calendar */}
            <div className="card p-4">
                <div className="flex items-start gap-4">
                    <div className="p-3 bg-primary-50 dark:bg-primary-600/20 rounded-lg">
                        <Calendar className="w-6 h-6 text-primary-600" />
                    </div>
                    <div className="flex-1">
                        <h3 className="font-semibold flex items-center gap-2">
                            Google Calendar
                            {status?.connected && (
                                <span className="text-xs px-2 py-0.5 bg-success-100 dark:bg-success-600/20 text-success-600 rounded-full">
                                    Connected
                                </span>
                            )}
                        </h3>
                        <p className="text-sm text-neutral-600 dark:text-neutral-400 mt-1">
                            Sync task deadlines to your Google Calendar. Get reminders 1 day and 1 hour before deadlines.
                        </p>

                        <div className="mt-4 flex flex-wrap gap-2">
                            {!status?.connected ? (
                                <button
                                    onClick={handleConnect}
                                    disabled={isConnecting}
                                    className="btn-primary flex items-center gap-2"
                                >
                                    {isConnecting ? (
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : (
                                        <Link2 className="w-4 h-4" />
                                    )}
                                    Connect Google Calendar
                                </button>
                            ) : (
                                <>
                                    <button
                                        onClick={handleSyncAll}
                                        disabled={isSyncing}
                                        className="btn-secondary flex items-center gap-2"
                                    >
                                        {isSyncing ? (
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                        ) : (
                                            <RefreshCw className="w-4 h-4" />
                                        )}
                                        Sync All Tasks
                                    </button>
                                    <button
                                        onClick={handleDisconnect}
                                        className="btn-ghost text-danger-600 flex items-center gap-2"
                                    >
                                        <Unlink className="w-4 h-4" />
                                        Disconnect
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Google Tasks */}
            <div className="card p-4">
                <div className="flex items-start gap-4">
                    <div className="p-3 bg-blue-50 dark:bg-blue-600/20 rounded-lg">
                        <svg className="w-6 h-6 text-blue-600" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M22 5.18L10.59 16.6l-4.24-4.24 1.41-1.41 2.83 2.83 10-10L22 5.18zm-2.21 5.04c.13.57.21 1.17.21 1.78 0 4.42-3.58 8-8 8s-8-3.58-8-8 3.58-8 8-8c1.58 0 3.04.46 4.28 1.25l1.44-1.44A9.9 9.9 0 0012 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10c0-1.19-.22-2.33-.6-3.39l-1.61 1.61z" />
                        </svg>
                    </div>
                    <div className="flex-1">
                        <h3 className="font-semibold">Google Tasks</h3>
                        <p className="text-sm text-neutral-600 dark:text-neutral-400 mt-1">
                            Two-way sync between VisionBoard and Google Tasks. Manage tasks from either platform.
                        </p>
                        <div className="mt-4">
                            <a
                                href="/integrations/google-tasks"
                                className="btn-primary inline-flex items-center gap-2"
                            >
                                <Link2 className="w-4 h-4" />
                                Configure Google Tasks
                            </a>
                        </div>
                    </div>
                </div>
            </div>

            {/* Coming Soon */}
            <div className="card p-4 opacity-60">
                <div className="flex items-start gap-4">
                    <div className="p-3 bg-neutral-100 dark:bg-neutral-700 rounded-lg">
                        <Bell className="w-6 h-6 text-neutral-500" />
                    </div>
                    <div>
                        <h3 className="font-semibold">Slack / Telegram</h3>
                        <p className="text-sm text-neutral-600 dark:text-neutral-400 mt-1">
                            Get deadline notifications via Slack or Telegram.
                        </p>
                        <span className="text-xs text-primary-600 mt-2 inline-block">Coming soon</span>
                    </div>
                </div>
            </div>
        </div>
    );
}
